import { jsx as _jsx } from "react/jsx-runtime";
import { pad2, splitUnits } from './countdownClock.js';
import { CIRCLES, PRESETS } from './countdownEngine.js';
const HEX_COLOR = /^#[0-9a-fA-F]{3}(?:[0-9a-fA-F]{3})?$/;
const TIMER_TYPES = ['DATE', 'EVERGREEN', 'DAILY'];
const EXPIRIES = ['HIDE', 'ZERO', 'RESTART'];
/** Defensive wire → badge payload. Anything malformed = no chip (never a throw on a public page). */
export function parseCountdownBadge(raw) {
    if (raw === null || typeof raw !== 'object')
        return undefined;
    const r = raw;
    const campaignId = typeof r.campaignId === 'string' && r.campaignId.trim() !== '' ? r.campaignId : undefined;
    const timerType = TIMER_TYPES.find((t) => t === r.timerType);
    const serverNowEpochMs = typeof r.serverNowEpochMs === 'number' && Number.isFinite(r.serverNowEpochMs)
        ? r.serverNowEpochMs
        : undefined;
    if (!campaignId || !timerType || serverNowEpochMs === undefined)
        return undefined;
    const endsAtEpochMs = typeof r.endsAtEpochMs === 'number' && Number.isFinite(r.endsAtEpochMs)
        ? r.endsAtEpochMs
        : undefined;
    const minutes = typeof r.minutes === 'number' && Number.isFinite(r.minutes) && r.minutes > 0
        ? r.minutes
        : undefined;
    if (timerType === 'EVERGREEN' ? minutes === undefined : endsAtEpochMs === undefined)
        return undefined;
    const design = (typeof r.design === 'object' && r.design !== null ? r.design : {});
    const rawColors = (typeof design.colors === 'object' && design.colors !== null ? design.colors : {});
    const colors = {};
    for (const key of ['background', 'text', 'digit', 'digitBackground']) {
        const value = rawColors[key];
        if (typeof value === 'string' && HEX_COLOR.test(value))
            colors[key] = value;
    }
    return {
        campaignId,
        timerType,
        serverNowEpochMs,
        endsAtEpochMs,
        minutes,
        onExpiry: EXPIRIES.find((e) => e === r.onExpiry) ?? 'HIDE',
        design: {
            preset: typeof design.preset === 'string' && design.preset !== '' ? design.preset : 'circles',
            colors,
        },
    };
}
/** Parse the whole `productId → payload` map once, at context build. */
export function parseCountdownBadges(raw) {
    if (raw === null || typeof raw !== 'object')
        return {};
    const out = {};
    for (const [productId, value] of Object.entries(raw)) {
        const badge = parseCountdownBadge(value);
        if (badge)
            out[productId] = badge;
    }
    return out;
}
/** The chip's first-render seconds — server props only (the ticker takes over after paint). */
function initialSeconds(badge) {
    if (badge.timerType === 'EVERGREEN')
        return (badge.minutes ?? 0) * 60;
    return Math.max(0, Math.floor(((badge.endsAtEpochMs ?? 0) - badge.serverNowEpochMs) / 1000));
}
/**
 * The chip. `HH:MM:SS` when under a day, `Jd HH:MM` beyond — a card has no room for four labelled cells,
 * and a chip reading "02 : 23 : 59 : 44" over a photo is noise, not urgency.
 */
export function CountdownBadge({ badge }) {
    const palette = PRESETS[badge.design.preset] ?? CIRCLES;
    const colors = badge.design.colors ?? {};
    const background = colors.background ?? (palette.background === 'transparent' ? '#111111' : palette.background);
    const digit = colors.digit ?? (palette.background === 'transparent' ? '#ffffff' : palette.digit);
    const units = splitUnits(initialSeconds(badge));
    const text = units.days > 0
        ? `${units.days}j ${pad2(units.hours)}:${pad2(units.minutes)}`
        : `${pad2(units.hours)}:${pad2(units.minutes)}:${pad2(units.seconds)}`;
    const style = {
        position: 'absolute',
        top: 8,
        insetInlineStart: 8,
        zIndex: 2,
        background,
        color: digit,
        borderRadius: 4,
        padding: '3px 7px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
        fontVariantNumeric: 'tabular-nums',
        pointerEvents: 'none',
    };
    return (_jsx("span", { style: style, dir: "ltr", "data-aqx-cd-badge": badge.campaignId, "data-cd-type": badge.timerType, "data-cd-expiry": badge.onExpiry, "data-cd-server-now": badge.serverNowEpochMs, "data-cd-ends-at": badge.endsAtEpochMs ?? '', "data-cd-minutes": badge.minutes ?? '', children: text }));
}
/** The ONE entry a product card calls: the chip for this product, or nothing. */
export function renderCountdownBadge(badges, productId) {
    if (!badges || !productId)
        return null;
    const badge = badges[productId];
    return badge ? _jsx(CountdownBadge, { badge: badge }) : null;
}
//# sourceMappingURL=CountdownBadge.js.map